import React from 'react';
import DsButton from '../../common/DsButton';

type FlyerSide = 'front' | 'back';

type EventDetailsFlyerView = {
  EventFlyerMedia: React.ElementType;
  canEditEventInfo: boolean;
  eventData: unknown;
  eventReadOnlyTitle?: string;
  flyerActionLoading: Record<FlyerSide, boolean>;
  flyerSideText: Record<FlyerSide, string>;
  flyerUrls: Record<FlyerSide, string>;
  handleFlyerDelete: (side: FlyerSide) => void;
  handlePerformanceFlyerDelete: () => void;
  isMultiPerformanceEvent: boolean;
  openFlyerPreview: (side: FlyerSide) => void;
  performanceFlyerLoading: boolean;
  promptFlyerUpload: (side: FlyerSide) => void;
  promptPerformanceFlyerUpload: () => void;
  renderInheritanceNotice: (label: string) => React.ReactNode;
  selectedMultiPerformance: unknown;
  selectedMultiPerformanceFlyerUrl?: string;
  selectedMultiPerformancePlaceholderEvent: unknown;
  usesParentAssets: boolean;
};

type EventDetailsFlyerPanelProps = {
  view: EventDetailsFlyerView;
  selectedPerformanceName: string;
};

export default function EventDetailsFlyerPanel({
  view,
  selectedPerformanceName,
}: EventDetailsFlyerPanelProps) {
  const {
    EventFlyerMedia,
    canEditEventInfo,
    eventData,
    eventReadOnlyTitle,
    flyerActionLoading,
    flyerSideText,
    flyerUrls,
    handleFlyerDelete,
    handlePerformanceFlyerDelete,
    isMultiPerformanceEvent,
    openFlyerPreview,
    performanceFlyerLoading,
    promptFlyerUpload,
    promptPerformanceFlyerUpload,
    renderInheritanceNotice,
    selectedMultiPerformance,
    selectedMultiPerformanceFlyerUrl,
    selectedMultiPerformancePlaceholderEvent,
    usesParentAssets,
  } = view;

  // マルチ公演のフライヤーは上部1枠に統一し、選択中の公演で切り替える。
  const hasCommonFlyer = Boolean(flyerUrls.front || flyerUrls.back);
  const isPerformanceSelected = Boolean(isMultiPerformanceEvent && selectedMultiPerformance);
  let topFlyerKind = 'common';
  if (isPerformanceSelected) {
    if (selectedMultiPerformanceFlyerUrl) topFlyerKind = 'performance';
    else if (hasCommonFlyer) topFlyerKind = 'common-fallback';
    else topFlyerKind = 'performance-placeholder';
  }
  const canEditPerformanceFlyer = canEditEventInfo && !usesParentAssets;
  const flyerSides: FlyerSide[] = ['front', 'back'];

  return (
    <div className="flyer-panel" id="event-flyer-section">
      <div className="flyer-section">
        {topFlyerKind === 'performance' || topFlyerKind === 'performance-placeholder' ? (
          <figure className="flyer-item flyer-item--performance">
            <div className="flyer-slot">
              <EventFlyerMedia
                event={selectedMultiPerformancePlaceholderEvent}
                imageUrl={topFlyerKind === 'performance' ? selectedMultiPerformanceFlyerUrl : ''}
                imageAlt={`${selectedPerformanceName}のフライヤー`}
                sizeVariant="card"
                borderRadius={12}
                imageClassName="flyer-performance-image"
                placeholderClassName="flyer-performance-placeholder"
                hasImage={topFlyerKind === 'performance'}
              />
            </div>
            <figcaption className="flyer-item__caption">
              {topFlyerKind === 'performance'
                ? `${selectedPerformanceName} 専用フライヤー`
                : `${selectedPerformanceName}（専用フライヤー未登録）`}
            </figcaption>
            {canEditPerformanceFlyer ? (
              <div className="flyer-item__actions">
                <DsButton
                  variant={topFlyerKind === 'performance' ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={promptPerformanceFlyerUpload}
                  disabled={performanceFlyerLoading}
                  title={!canEditEventInfo ? eventReadOnlyTitle : undefined}
                >
                  <i className="fa-solid fa-image" aria-hidden="true"></i>
                  <span>
                    {topFlyerKind === 'performance'
                      ? '変更'
                      : `${selectedPerformanceName} のフライヤーを登録`}
                  </span>
                </DsButton>
                {topFlyerKind === 'performance' ? (
                  <DsButton
                    variant="danger"
                    size="sm"
                    onClick={handlePerformanceFlyerDelete}
                    disabled={performanceFlyerLoading}
                    title={!canEditEventInfo ? eventReadOnlyTitle : undefined}
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    <span>削除</span>
                  </DsButton>
                ) : null}
              </div>
            ) : null}
          </figure>
        ) : (
          <>
            <div className="flyer-stack">
              {flyerSides.map((side) => {
                const url = flyerUrls[side];
                const label = flyerSideText[side];
                const loading = flyerActionLoading[side];
                const editable = canEditEventInfo && !usesParentAssets;
                return (
                  <figure className="flyer-item" key={side}>
                    <div className="flyer-slot">
                      {url ? (
                        <EventFlyerMedia
                          event={eventData}
                          imageUrl={url}
                          imageAlt={`${label}のフライヤー`}
                          sizeVariant={side === 'front' ? 'card' : 'large'}
                          borderRadius={12}
                          imageClassName="flyer-image"
                          onImageClick={() => openFlyerPreview(side)}
                        />
                      ) : editable ? (
                        <DsButton
                          variant="secondary"
                          size="sm"
                          className="flyer-slot__add"
                          onClick={() => promptFlyerUpload(side)}
                          aria-label={`${label}を登録`}
                          disabled={loading}
                        >
                          <i className="fa-solid fa-image" aria-hidden="true"></i>
                          <span>{label}を登録</span>
                        </DsButton>
                      ) : (
                        <div className="flyer-slot__empty">
                          <i className="fa-regular fa-image" aria-hidden="true"></i>
                          <span>未登録</span>
                        </div>
                      )}
                    </div>
                    <figcaption className="flyer-item__caption">{label}</figcaption>
                    {url && editable ? (
                      <div className="flyer-item__actions">
                        <DsButton
                          variant="secondary"
                          size="sm"
                          onClick={() => promptFlyerUpload(side)}
                          disabled={loading}
                          aria-label={`${label}のフライヤーを差し替え`}
                        >
                          <i className="fa-solid fa-image" aria-hidden="true"></i>
                          <span>変更</span>
                        </DsButton>
                        <DsButton
                          variant="danger"
                          size="sm"
                          onClick={() => handleFlyerDelete(side)}
                          disabled={loading}
                          aria-label={`${label}のフライヤーを削除`}
                        >
                          <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          <span>削除</span>
                        </DsButton>
                      </div>
                    ) : null}
                  </figure>
                );
              })}
            </div>
            {topFlyerKind === 'common-fallback' ? (
              <div className="flyer-performance-fallback">
                <p className="flyer-performance-fallback__note">
                  <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
                  <span>
                    {selectedPerformanceName} は専用フライヤー未登録のため、イベント共通フライヤーを表示しています。
                  </span>
                </p>
                {canEditPerformanceFlyer ? (
                  <DsButton
                    size="sm"
                    onClick={promptPerformanceFlyerUpload}
                    disabled={performanceFlyerLoading}
                    title={!canEditEventInfo ? eventReadOnlyTitle : undefined}
                  >
                    <i className="fa-solid fa-image" aria-hidden="true"></i>
                    <span>{selectedPerformanceName} に専用フライヤーを登録</span>
                  </DsButton>
                ) : null}
              </div>
            ) : null}
          </>
        )}
        {usesParentAssets ? renderInheritanceNotice('フライヤー') : null}
      </div>
    </div>
  );
}
